/**
 * [TD-47.6a-ui] PlayerAnalysisCanvas - Zone 2 permanent surface.
 *
 * Two-tab structure (E1-CORRECTION, founder-locked):
 *   • Tab 1 "Player Stats"   - free tier. Existing PlayerStatsTab -
 *                              basic stats (PPG, ADP, Bye, position-
 *                              specific stats, below-fold detail grid).
 *   • Tab 2 "PGF Analytics"  - paid tier. Radar + position gauges +
 *                              gold/silver + weekly bar + PGF Analysis
 *                              paragraph + deep-dive. Unpaid users see
 *                              a blur+CTA paywall overlay (the
 *                              conversion mechanism).
 *
 * Tab 1 fetches /api/players/{id}/metrics (basic stats).
 * Tab 2 owns its own fetch (canvas + LLM fact-packet).
 *
 * The Tab 1 metrics fetch runs only when Tab 1 is active, Tab 2 owns
 * its own fetch; this keeps tab-switching responsive and avoids
 * fetching paid-tier data for non-paid users.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { authHeaders } from "../../contexts/AuthContext";
import { useAuth } from "../../contexts/AuthContext";
import { useEntitlements } from "../../hooks/useEntitlements";
import PlayerStatsTab from "./PlayerStatsTab";
import PGFAnalyticsTab from "../analytics/PGFAnalyticsTab";
import TabErrorBoundary from "../analytics/TabErrorBoundary";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TABS = [
  { id: "stats",     label: "Player Stats"  },
  { id: "analytics", label: "PGF Analytics" },
];

export default function PlayerAnalysisCanvas({ leagueId, currentPlayer }) {
  const { user } = useAuth();
  const { hasDraftAnalytics } = useEntitlements();
  // [E1-CORRECTION Fix 8] PGF Analytics is the DEFAULT active tab for
  // paid/alpha users. Free-tier users default to Player Stats since the
  // PGF Analytics tab will paywall-blur their content.
  const isPaid =
    user?.role === "admin" || !!user?.alpha_flags?.pgf_analytics_alpha || hasDraftAnalytics;
  const [activeTab, setActiveTab] = useState(isPaid ? "analytics" : "stats");
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!currentPlayer?.id || !leagueId) {
      setData(null);
      return;
    }
    let alive = true;
    setLoading(true);
    setErr(null);
    (async () => {
      try {
        const res = await axios.get(
          `${API}/players/${currentPlayer.id}/metrics`,
          { params: { league_id: leagueId }, headers: authHeaders() },
        );
        if (alive) setData(res.data);
      } catch (e) {
        if (alive) setErr(e?.response?.data?.detail || "Failed to load metrics");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [currentPlayer?.id, leagueId]);

  return (
    <div
      className="bg-[#1C1C1C] border border-[#252525] rounded-2xl flex flex-col h-full overflow-hidden"
      data-testid="player-analysis-canvas"
    >
      {/* Header strip + tab bar */}
      <div className="px-3 pt-2.5 border-b border-[#252525] shrink-0">
        <div className="flex items-center justify-between mb-2">
          <span className="font-display font-bold text-xs uppercase tracking-wider text-[#FF6B00]">
            Player Analysis
          </span>
          <span className="font-body text-[10px] text-[#A0A0A0]">
            {loading && activeTab === "stats" ? "Loading…" :
             data?.season_year && activeTab === "stats" ? `${data.season_year} stats` : ""}
          </span>
        </div>
        <div className="flex gap-1" data-testid="player-analysis-tab-bar">
          {TABS.map((tab) => {
            const isActive = activeTab === tab.id;
            return (
              <button
                type="button"
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                data-testid={`player-analysis-tab-${tab.id}`}
                data-active={isActive}
                className={`px-3 py-1.5 font-display font-bold text-[11px] uppercase tracking-wider border-b-2 transition-colors ${
                  isActive
                    ? "text-[#FF6B00] border-[#FF6B00]"
                    : "text-[#A0A0A0] border-transparent hover:text-[#F5F5F5]"
                }`}
              >
                {tab.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-hidden">
        {!currentPlayer ? (
          <div
            className="h-full flex flex-col items-center justify-center text-center px-4"
            data-testid="player-analysis-empty"
          >
            <p className="font-display font-bold text-sm uppercase tracking-wider text-[#A0A0A0]">
              Click any player
            </p>
            <p className="font-body text-xs text-[#666] mt-2 max-w-xs">
              Stats appear here when you tap a row in the pool.
            </p>
          </div>
        ) : activeTab === "stats" ? (
          err ? (
            <div className="h-full flex items-center justify-center px-4">
              <p className="font-body text-xs text-[#FF4444]" data-testid="player-analysis-error">
                {err}
              </p>
            </div>
          ) : data ? (
            <PlayerStatsTab
              playerInfo={data.player_info}
              metrics={data.metrics}
              seasonYear={data.season_year}
              dataAvailability={data.data_availability}
              scoringFormat={data.scoring_format_used}
            />
          ) : (
            <div className="h-full flex items-center justify-center">
              <div className="w-6 h-6 border-2 border-[#FF6B00] border-t-transparent rounded-full animate-spin" />
            </div>
          )
        ) : (
          <TabErrorBoundary
            resetKey={currentPlayer?.id}
            testid="pgf-analytics-error-boundary"
            fallbackLabel="Analytics unavailable"
            fallbackHint="This player's PGF Analytics hit a render error. Pick another player or reload - the rest of the draft is still running."
          >
            <PGFAnalyticsTab
              leagueId={leagueId}
              currentPlayer={currentPlayer}
              basicMetrics={data}
              metricsLoading={loading}
              onUpgrade={() => { /* TD-2d Stripe wires here */ }}
            />
          </TabErrorBoundary>
        )}
      </div>
    </div>
  );
}
